import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Download,
  ChevronRight,
  MessageSquare,
  Star,
  Bug,
  Lightbulb,
  Palette,
  HelpCircle,
} from 'lucide-react';
import RefreshButton from '../../components/layout/RefreshButton';
import Select from '../../components/ui/Select';
import { adminApi } from '../../services/adminApi';
import { exportToCSV, exportToJSON, prepareFeedbackForExport } from '../../utils/export';

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'bug', label: 'Bug Report' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'ux', label: 'UX Feedback' },
  { value: 'general', label: 'General' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'new', label: 'New' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
];

const TYPE_ICONS = {
  bug: Bug,
  feature: Lightbulb,
  ux: Palette,
  general: HelpCircle,
};

export default function FeedbackList() {
  const [feedback, setFeedback] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchFeedback = useCallback(async () => {
    try {
      const response = await adminApi.getFeedback({
        page,
        limit: 20,
        type: typeFilter || undefined,
        status: statusFilter || undefined,
      });
      setFeedback(response.feedback || []);
      setTotalPages(response.totalPages || 1);
      setTotal(response.total || 0);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch feedback:', err);
    }
  }, [page, typeFilter, statusFilter]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await fetchFeedback();
      setIsLoading(false);
    };
    loadData();
  }, [fetchFeedback]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchFeedback();
    setIsRefreshing(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'bug':
        return 'bg-red-100 text-red-600';
      case 'feature':
        return 'bg-green-100 text-green-600';
      case 'ux':
        return 'bg-purple-100 text-purple-600';
      default:
        return 'bg-blue-100 text-blue-600';
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await adminApi.getFeedback({ limit: 10000 });
      const exportData = prepareFeedbackForExport(response.feedback || []);

      if (format === 'csv') {
        exportToCSV(exportData, 'feedback-export', [
          { key: 'type', label: 'Type' },
          { key: 'rating', label: 'Rating' },
          { key: 'message', label: 'Message' },
          { key: 'userEmail', label: 'User Email' },
          { key: 'status', label: 'Status' },
          { key: 'submittedDate', label: 'Submitted Date' },
        ]);
      } else {
        exportToJSON(exportData, 'feedback-export');
      }
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">User Feedback</h1>
          <p className="text-text-secondary mt-1">
            Review and manage user feedback ({total} total)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <RefreshButton
            onClick={handleRefresh}
            isLoading={isRefreshing}
            lastUpdated={lastUpdated}
          />
          <div className="relative group">
            <button className="btn-secondary">
              <Download className="w-4 h-4" />
              Export
            </button>
            <div className="absolute right-0 mt-2 w-32 bg-white rounded-lg shadow-lg border border-border py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
              <button
                onClick={() => handleExport('csv')}
                className="w-full px-4 py-2 text-left text-sm hover:bg-bg-secondary"
              >
                Export CSV
              </button>
              <button
                onClick={() => handleExport('json')}
                className="w-full px-4 py-2 text-left text-sm hover:bg-bg-secondary"
              >
                Export JSON
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="flex gap-4">
          <div className="min-w-[180px]">
            <Select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
              options={TYPE_OPTIONS}
              placeholder="All Types"
            />
          </div>
          <div className="min-w-[160px]">
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              options={STATUS_OPTIONS}
              placeholder="All Status"
            />
          </div>
        </div>
      </div>

      {/* Feedback List */}
      <div className="card">
        {isLoading ? (
          <div className="p-4">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-20 bg-bg-secondary rounded"></div>
              ))}
            </div>
          </div>
        ) : feedback.length === 0 ? (
          <div className="p-12 text-center text-text-muted">
            No feedback found
          </div>
        ) : (
          <div className="divide-y divide-border">
            {feedback.map((item) => {
              const TypeIcon = TYPE_ICONS[item.feedbackType] || MessageSquare;
              return (
                <Link
                  key={item.id}
                  to={`/feedback/${item.id}`}
                  className="p-4 flex items-start gap-4 hover:bg-bg-secondary/50 transition-colors"
                >
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getTypeColor(
                      item.feedbackType
                    )}`}
                  >
                    <TypeIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-text-primary capitalize">
                          {item.feedbackType || 'General'} Feedback
                        </span>
                        <span
                          className={`badge ${
                            item.status === 'new'
                              ? 'badge-warning'
                              : item.status === 'resolved'
                              ? 'badge-success'
                              : 'badge-primary'
                          }`}
                        >
                          {item.status || 'new'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-4 h-4 ${
                              star <= (item.rating || 0)
                                ? 'text-amber-400 fill-amber-400'
                                : 'text-gray-200'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-text-muted line-clamp-2 mb-2">
                      {item.message}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-text-muted">
                      <span>From: {item.userEmail || 'Anonymous'}</span>
                      <span>{formatDate(item.createdAt)}</span>
                      {item.attachments?.length > 0 && (
                        <span>{item.attachments.length} attachments</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-text-muted flex-shrink-0" />
                </Link>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-border flex items-center justify-between">
            <p className="text-sm text-text-muted">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-ghost"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="btn-ghost"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
